import React, { useEffect, useState, useRef } from 'react';
import { db, collection, onSnapshot, query, handleFirestoreError, OperationType } from '../firebase';
import { Client } from '../types';
import { useAuth } from '../App';
import { BellRing, ShieldAlert, X, Check, Volume2, VolumeX, Calendar, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

// Reusable target roles for access control
const CONTRACT_ROLES = ['admin', 'manager', 'billing', 'accountant', 'ops_manager', 'md', 'technical', 'customer_care'];

// Helper to calculate remaining time
export interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
}

export function calculateTimeRemaining(endDateStr: string): TimeRemaining {
  const now = new Date();
  // Parse end date at 23:59:59 to give full end-of-day credit
  const end = new Date(endDateStr + 'T23:59:59');
  const diffMs = end.getTime() - now.getTime();
  
  if (diffMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds, totalSeconds };
}

// Determine active cycle key based on remaining days
export function getReminderCycleKey(days: number): string | null {
  if (days <= 0) return null;
  if (days <= 10) {
    // 10 days or less: remind every day (unique key per day remaining)
    return `day-${days}`;
  }
  if (days <= 30) {
    // 30 to 11 days: remind every 10 days
    // Map to specific 10-day brackets:
    // 21-30 days -> bracket '30-days'
    // 11-20 days -> bracket '20-days'
    const bracket = Math.ceil(days / 10) * 10;
    return `${bracket}-days`;
  }
  if (days <= 90) {
    // 90 to 31 days: remind every month (30 days)
    // Map to specific 30-day brackets:
    // 61-90 days -> bracket '3-months'
    // 31-60 days -> bracket '2-months'
    const bracket = Math.ceil(days / 30);
    return `${bracket}-months`;
  }
  return null; // Over 90 days: no active reminder
}

// Global Audio player to avoid duplicate intervals
let audioInterval: any = null;
let audioCtx: AudioContext | null = null;
let isAudioMuted = false;

const startAlarmSound = () => {
  if (audioInterval || isAudioMuted) return;
  
  const playPulse = () => {
    if (isAudioMuted) return;
    try {
      if (!audioCtx) {
        audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
      
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      
      osc.type = 'sine';
      // Dual beeps for urgency
      osc.frequency.setValueAtTime(987.77, audioCtx.currentTime); // B5 note
      
      gain.gain.setValueAtTime(0.12, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.35);
      
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      
      osc.start();
      osc.stop(audioCtx.currentTime + 0.4);
    } catch (e) {
      // Autoplay blocker handled gracefully
    }
  };

  playPulse();
  audioInterval = setInterval(playPulse, 2500);
};

const stopAlarmSound = () => {
  if (audioInterval) {
    clearInterval(audioInterval);
    audioInterval = null;
  }
};

export const ContractReminders: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [muteState, setMuteState] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<Record<string, boolean>>({});
  const { profile, user } = useAuth();
  
  // Real-time ticking trigger
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch clients with contract info
  useEffect(() => {
    if (!user) return;
    
    const unsubscribe = onSnapshot(collection(db, 'clients'), (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client));
      setClients(list.filter(c => c.contractEndDate));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'clients');
    });

    return unsubscribe;
  }, [user]);

  // Load checked reminders from localStorage
  useEffect(() => {
    if (!user) return;
    const stored = localStorage.getItem(`checked_contract_reminders_${user.uid}`);
    if (stored) {
      try {
        setCheckedKeys(JSON.parse(stored));
      } catch (e) {
        // Handle gracefully
      }
    }
  }, [user]);

  // Save checked reminders to localStorage
  const saveCheckedKeys = (keys: Record<string, boolean>) => {
    if (!user) return;
    setCheckedKeys(keys);
    localStorage.setItem(`checked_contract_reminders_${user.uid}`, JSON.stringify(keys));
  };

  // Check if current user role has access
  const hasAccess = profile && CONTRACT_ROLES.includes(profile.role);

  // Compute active reminders list
  const reminders = clients
    .map(client => {
      const timeRemaining = calculateTimeRemaining(client.contractEndDate || '');
      const cycleKey = getReminderCycleKey(timeRemaining.days);
      const isUrgent = timeRemaining.days <= 10;
      
      return {
        client,
        timeRemaining,
        cycleKey,
        isUrgent,
        uniqueKey: cycleKey ? `${client.id}-${cycleKey}` : null
      };
    })
    .filter(item => item.cycleKey !== null); // Only items inside the active <= 90 days windows

  // Unread reminders are those not yet clicked/checked
  const unreadReminders = reminders.filter(r => r.uniqueKey && !checkedKeys[r.uniqueKey]);

  // Control the alarm sound based on unread reminders and mute state
  useEffect(() => {
    if (hasAccess && unreadReminders.length > 0 && !muteState) {
      isAudioMuted = false;
      startAlarmSound();
    } else {
      stopAlarmSound();
    }
    return () => stopAlarmSound();
  }, [unreadReminders.length, muteState, hasAccess]);

  const handleCheckReminder = (uniqueKey: string) => {
    const updated = { ...checkedKeys, [uniqueKey]: true };
    saveCheckedKeys(updated);
    toast.success('Reminder acknowledged. Alert sound paused for this cycle.');
  };

  const handleToggleMute = () => {
    const nextMute = !muteState;
    setMuteState(nextMute);
    isAudioMuted = nextMute;
    if (nextMute) {
      stopAlarmSound();
      toast.info('Alarm sound muted.');
    } else {
      toast.info('Alarm sound unmuted.');
    }
  };

  if (!hasAccess) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-xl transition-all md:p-2.5 flex items-center justify-center cursor-pointer ${
          unreadReminders.length > 0
            ? "bg-red-600 text-white animate-pulse border border-red-700 shadow-md shadow-red-900/30"
            : "bg-white/10 text-white hover:bg-white/20"
        }`}
        id="btn-contract-reminders"
      >
        <BellRing className={`w-5 h-5 md:w-6 md:h-6 ${unreadReminders.length > 0 ? "animate-bounce" : ""}`} />
        {unreadReminders.length > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 text-slate-900 text-[9px] font-black flex items-center justify-center rounded-full border-2 border-red-600 shadow-lg animate-bounce">
            {unreadReminders.length}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-[100]"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 mt-3 w-[calc(100vw-2rem)] md:w-[420px] bg-white rounded-[32px] shadow-2xl border border-slate-100 z-[101] overflow-hidden"
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-red-600 text-white">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 animate-bounce" />
                  <h3 className="font-black tracking-tight">Contract Expiration Alerts</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleToggleMute}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-all"
                    title={muteState ? "Unmute Alarm" : "Mute Alarm"}
                  >
                    {muteState ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
                  </button>
                  <span className="text-[10px] font-black text-red-600 bg-white px-3 py-1 rounded-full uppercase tracking-widest">
                    {unreadReminders.length} Pending
                  </span>
                </div>
              </div>

              {/* Scrollable reminders list */}
              <div className="max-h-[420px] overflow-y-auto p-4 space-y-4 bg-slate-50">
                {reminders.length > 0 ? (
                  reminders.map(({ client, timeRemaining, uniqueKey, isUrgent }, idx) => {
                    const isChecked = uniqueKey ? checkedKeys[uniqueKey] : false;
                    return (
                      <div
                        key={`${client.id}-${uniqueKey || 'none'}-${idx}`}
                        className={`p-5 rounded-2xl border transition-all ${
                          isChecked 
                            ? 'bg-white border-slate-100 opacity-60' 
                            : isUrgent
                              ? 'bg-red-50/50 border-red-200 shadow-md shadow-red-50'
                              : 'bg-white border-slate-100 shadow-sm'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <span className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-1">
                              {client.serviceType || 'Client Service'}
                            </span>
                            <h4 className="font-black text-slate-900 tracking-tight text-base mb-1">{client.name}</h4>
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium mb-3">
                              <Calendar className="w-3.5 h-3.5 text-slate-400" />
                              <span>Expires: {client.contractEndDate}</span>
                            </div>
                          </div>
                          
                          {uniqueKey && !isChecked && (
                            <button
                              onClick={() => handleCheckReminder(uniqueKey)}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-emerald-700 transition-all flex items-center gap-1 shrink-0"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Check
                            </button>
                          )}
                        </div>

                        {/* Realtime Countdown */}
                        <div className="p-3 bg-slate-900 text-white rounded-xl font-mono flex items-center justify-between text-center">
                          <div>
                            <span className="text-[18px] font-black block leading-none">{timeRemaining.days}</span>
                            <span className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">Days</span>
                          </div>
                          <span className="text-slate-600 font-black">:</span>
                          <div>
                            <span className="text-[18px] font-black block leading-none">
                              {String(timeRemaining.hours).padStart(2, '0')}
                            </span>
                            <span className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">Hours</span>
                          </div>
                          <span className="text-slate-600 font-black">:</span>
                          <div>
                            <span className="text-[18px] font-black block leading-none">
                              {String(timeRemaining.minutes).padStart(2, '0')}
                            </span>
                            <span className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">Mins</span>
                          </div>
                          <span className="text-slate-600 font-black">:</span>
                          <div>
                            <span className="text-[18px] font-black block leading-none text-red-400">
                              {String(timeRemaining.seconds).padStart(2, '0')}
                            </span>
                            <span className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">Secs</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-10 text-center">
                    <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Calendar className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm text-slate-500 font-bold">No expiring contracts within 3 months</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// Sleek layout countdown banner that displays counting down of closest expiring contracts
export const ContractCountdownBanner: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const { profile, user } = useAuth();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) return;
    
    const unsubscribe = onSnapshot(collection(db, 'clients'), (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client));
      setClients(list.filter(c => c.contractEndDate));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'clients');
    });

    return unsubscribe;
  }, [user]);

  const hasAccess = profile && CONTRACT_ROLES.includes(profile.role);
  if (!hasAccess) return null;

  // Find all clients expiring within 30 days that aren't past expired yet (or show them as zero/urgent if expiring in under 10 days)
  const expiringContracts = clients
    .map(client => {
      const timeRemaining = calculateTimeRemaining(client.contractEndDate || '');
      return { client, timeRemaining };
    })
    .filter(item => item.timeRemaining.totalSeconds > 0 && item.timeRemaining.days <= 90)
    .sort((a, b) => a.timeRemaining.totalSeconds - b.timeRemaining.totalSeconds);

  if (expiringContracts.length === 0) return null;

  // Get the most urgent expiring contract
  const urgent = expiringContracts[0];

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      className="bg-slate-900 border-b border-slate-800 text-white px-6 py-3.5 flex flex-col md:flex-row md:items-center justify-between gap-4 relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 bottom-0 left-0 bg-red-600/5 pointer-events-none animate-pulse" />
      <div className="flex items-center gap-3 relative z-10">
        <span className="flex h-3 w-3 relative shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
        </span>
        <div className="text-sm">
          <span className="font-bold text-slate-300">Contract Expiry Warning: </span>
          <span className="font-black text-red-400 underline decoration-red-500 decoration-2">{urgent.client.name}</span>
          <span className="text-slate-400 text-xs ml-2">(Ends {urgent.client.contractEndDate})</span>
        </div>
      </div>

      <div className="flex items-center gap-4 relative z-10">
        <span className="text-xs font-black uppercase tracking-widest text-slate-400">Countdown:</span>
        <div className="flex items-center gap-2 font-mono text-xs bg-slate-950/80 border border-slate-800 px-4 py-2 rounded-xl">
          <div>
            <span className="font-black text-[14px] text-white">{urgent.timeRemaining.days}</span>
            <span className="text-[9px] text-slate-500 ml-0.5">d</span>
          </div>
          <span className="text-slate-700">:</span>
          <div>
            <span className="font-black text-[14px] text-white">
              {String(urgent.timeRemaining.hours).padStart(2, '0')}
            </span>
            <span className="text-[9px] text-slate-500 ml-0.5">h</span>
          </div>
          <span className="text-slate-700">:</span>
          <div>
            <span className="font-black text-[14px] text-white">
              {String(urgent.timeRemaining.minutes).padStart(2, '0')}
            </span>
            <span className="text-[9px] text-slate-500 ml-0.5">m</span>
          </div>
          <span className="text-slate-700">:</span>
          <div>
            <span className="font-black text-[14px] text-red-400">
              {String(urgent.timeRemaining.seconds).padStart(2, '0')}
            </span>
            <span className="text-[9px] text-slate-500 ml-0.5">s</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
